// src/services/patientAppointmentWorkspace.js
//
// Appointment workspace calls (Patient Appointments Task 3).
//
// Rules:
//   * the patient is always the authenticated session -- no patient id is sent;
//   * a hold token is passed in and out of these functions only, never stored;
//   * a video credential is returned to the caller once and never stored;
//   * file uploads use an opaque storage reference -- no bucket or key exists
//     on this side;
//   * every read accepts an AbortSignal so a superseded request can be dropped.

import { api } from "../lib/patientApi";
import { patientInvoicesApi } from "./patientInvoices";

const BASE = "/api/v1/patients/me/appointments";

function clean(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && !(typeof value === "string" && value.trim() === ""))
  );
}

export const appointmentWorkspaceApi = {
  async list(params = {}, { signal } = {}) {
    const res = await api.get(BASE, { params: clean(params), signal });
    return res.data;
  },

  async filterOptions({ signal } = {}) {
    const res = await api.get(`${BASE}/filter-options`, { signal });
    return res.data;
  },

  async detail(appointmentId, { signal } = {}) {
    const res = await api.get(`${BASE}/${appointmentId}`, { signal });
    return res.data;
  },

  async paymentStatus(appointmentId, { signal } = {}) {
    const res = await api.get(`${BASE}/${appointmentId}/payments/status`, { signal });
    return res.data;
  },

  async startCheckout(appointmentId) {
    const res = await api.post(`${BASE}/${appointmentId}/payments/stripe/checkout`);
    return res.data;
  },

  async cancel(appointmentId, reason) {
    const res = await api.post(`${BASE}/${appointmentId}/cancel`, { reason: reason || null });
    return res.data;
  },

  async rescheduleSlots(appointmentId, params = {}, { signal } = {}) {
    const res = await api.get(`${BASE}/${appointmentId}/reschedule/slots`, { params: clean(params), signal });
    return res.data;
  },

  async createRescheduleHold(appointmentId, { slotInventoryId, consultationMode, practiceLocationId, displayTimezone, replaceExisting = false }) {
    const res = await api.post(`${BASE}/${appointmentId}/reschedule/holds`, {
      slot_inventory_id: slotInventoryId,
      consultation_mode: consultationMode || null,
      practice_location_id: practiceLocationId || null,
      display_timezone: displayTimezone || null,
      replace_existing: replaceExisting,
    });
    return res.data;
  },

  async releaseHold(holdId, holdToken) {
    const res = await api.post(`${BASE}/holds/${holdId}/release`, { hold_token: holdToken });
    return res.data;
  },

  async reschedule(appointmentId, { holdId, holdToken, slotInventoryId }) {
    const res = await api.post(`${BASE}/${appointmentId}/reschedule`, {
      hold_id: holdId,
      hold_token: holdToken,
      slot_inventory_id: slotInventoryId,
    });
    return res.data;
  },

  async forms(appointmentId, { signal } = {}) {
    const res = await api.get(`${BASE}/${appointmentId}/forms`, { signal });
    return res.data;
  },

  async saveDraft(appointmentId, assignmentId, answers) {
    const res = await api.put(`${BASE}/${appointmentId}/forms/${assignmentId}/draft`, { answers });
    return res.data;
  },

  async submitForm(appointmentId, assignmentId, answers) {
    const res = await api.post(`${BASE}/${appointmentId}/forms/${assignmentId}/submit`, { answers });
    return res.data;
  },

  async presignFile(appointmentId, assignmentId, { fieldKey, file }) {
    const res = await api.post(`${BASE}/${appointmentId}/forms/${assignmentId}/files/presign`, {
      field_key: fieldKey,
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
    });
    return res.data;
  },

  async uploadToSignedUrl(upload, file) {
    const res = await fetch(upload.upload_url, { method: "PUT", headers: upload.upload_headers || {}, body: file });
    if (!res.ok) {
      const error = new Error("upload failed");
      error.uploadFailed = true;
      throw error;
    }
  },

  async confirmFile(appointmentId, assignmentId, { fieldKey, storageReference, fileName }) {
    const res = await api.post(`${BASE}/${appointmentId}/forms/${assignmentId}/files/confirm`, {
      field_key: fieldKey,
      storage_reference: storageReference,
      file_name: fileName,
    });
    return res.data;
  },

  async fileDownloadUrl(appointmentId, assignmentId, storageReference) {
    const res = await api.get(`${BASE}/${appointmentId}/forms/${assignmentId}/files/${storageReference}`);
    return res.data;
  },

  async createVideoToken(appointmentId) {
    const res = await api.post(`${BASE}/${appointmentId}/video/token`);
    return res.data;
  },

  async videoSession(appointmentId, { signal } = {}) {
    const res = await api.get(`${BASE}/${appointmentId}/video/session`, { signal });
    return res.data;
  },

  async recordVideoEvent(appointmentId, eventType) {
    const res = await api.post(`${BASE}/${appointmentId}/video/events`, { event_type: eventType });
    return res.data;
  },

  // -> { blob, filename } (filename from Content-Disposition; JSON error bodies parsed).
  async downloadInvoicePdf(invoiceId, invoiceNumber) {
    return patientInvoicesApi.downloadPdf(invoiceId, invoiceNumber);
  },
};

export default appointmentWorkspaceApi;
